import { useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { isValidKey, generateKey } from '../utils/key'
import PublicLayout from '../components/layout/PublicLayout'

const CARD_BG = 'rgba(30, 41, 59, 0.66)'

const STEPS = [
  { n: '01', title: 'Create a key', body: 'One tap makes a private log — a memorable word-key in your URL. No email, no password, no sign-up.' },
  { n: '02', title: 'Log moves & meals', body: 'Tap to record cardio, weights or exercise. For food, just type it — AI returns the full nutrition to confirm.' },
  { n: '03', title: 'See the picture', body: 'Trends, calories in vs out, and personalised targets from your BMR/TDEE. Bookmark your key and go.' },
]

const FEATURES = [
  { icon: '🍔', title: 'AI nutrition', body: 'Type “chicken burrito and a coke” — calories, macros and micros, itemised for you to confirm.' },
  { icon: '🏋️', title: 'Every kind of move', body: 'Cardio, weights and general exercise, with autocomplete, history and meaningful charts.' },
  { icon: '🎯', title: 'Targets that fit you', body: 'BMR / TDEE from your profile and reference values — intake vs target and energy balance.' },
  { icon: '🔒', title: 'Yours alone', body: 'No account. Optional password lock with email reset, full export and automatic backups.' },
]

export default function Home() {
  const navigate = useNavigate()

  useEffect(() => {
    const hashKey = window.location.hash.slice(1)
    if (isValidKey(hashKey)) navigate(`/u/${hashKey}`, { replace: true })
  }, [navigate])

  const createNew = () => navigate(`/u/${generateKey()}?welcome=1`)
  const openExisting = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const key = new FormData(e.currentTarget).get('key')?.toString().trim()
    if (isValidKey(key)) navigate(`/u/${key}`)
  }

  return (
    <PublicLayout>
      <div className="mx-auto max-w-5xl px-6 pb-4">
        {/* Hero */}
        <section className="mx-auto mt-12 max-w-3xl text-center md:mt-20">
          <span className="reveal inline-block rounded-full border border-[var(--border)] bg-white/5 px-4 py-1.5 text-xs tracking-wide text-[var(--text-secondary)]" style={{ animationDelay: '0.05s' }}>
            No sign-up · no email · just a key
          </span>
          <h1 className="font-display reveal mt-6 text-5xl font-extrabold leading-[1.04] md:text-7xl" style={{ animationDelay: '0.12s' }}>
            Move more.
            <br />
            <span className="text-[var(--accent-teal)]">Eat</span> <span className="text-[var(--accent-orange)]">smarter</span>.
            <br />
            Stay private.
          </h1>
          <p className="reveal mx-auto mt-6 max-w-xl text-lg leading-relaxed text-[var(--text-secondary)]" style={{ animationDelay: '0.2s' }}>
            The private fitness &amp; nutrition log. Track your training, then just <em>type what you ate</em> and let AI
            break down the nutrition. Everything lives behind one secret key — no account, ever.
          </p>

          <div className="reveal mx-auto mt-10 max-w-md rounded-2xl border border-[var(--border)] p-5 shadow-2xl backdrop-blur-xl" style={{ background: CARD_BG, animationDelay: '0.28s' }}>
            <button onClick={createNew} className="btn btn-primary w-full py-3 text-base font-semibold">
              Start your log — free
            </button>
            <div className="my-3 flex items-center gap-3 text-xs text-[var(--text-secondary)]">
              <span className="h-px flex-1 bg-[var(--border)]" />
              or open an existing one
              <span className="h-px flex-1 bg-[var(--border)]" />
            </div>
            <form onSubmit={openExisting} className="flex gap-2">
              <input name="key" placeholder="word-word-word-word" autoComplete="off" className="flex-1" />
              <button className="btn btn-secondary">Open</button>
            </form>
          </div>
          <p className="reveal mt-3 text-xs text-[var(--text-secondary)]" style={{ animationDelay: '0.34s' }}>
            Lost your key? <Link to="/recover" className="text-[var(--accent-teal)] underline">Recover via email</Link>
          </p>
        </section>

        {/* How it works */}
        <section className="mt-28">
          <h2 className="font-display text-center text-3xl font-bold md:text-4xl">Logging that gets out of your way</h2>
          <p className="mx-auto mt-3 max-w-lg text-center text-[var(--text-secondary)]">Three steps. No friction. No forms full of dropdowns.</p>
          <div className="mt-10 grid gap-4 md:grid-cols-3">
            {STEPS.map((s) => (
              <div key={s.n} className="rounded-xl border border-[var(--border)] p-6 backdrop-blur-sm" style={{ background: CARD_BG }}>
                <div className="font-display text-3xl font-extrabold text-[var(--accent-teal)]">{s.n}</div>
                <h3 className="font-display mt-3 text-lg font-bold">{s.title}</h3>
                <p className="mt-1 text-sm leading-relaxed text-[var(--text-secondary)]">{s.body}</p>
              </div>
            ))}
          </div>
          <div className="mt-6 text-center">
            <Link to="/how-it-works" className="text-sm text-[var(--accent-teal)] hover:underline">See how it works →</Link>
          </div>
        </section>

        {/* Features */}
        <section className="mt-24 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {FEATURES.map((f) => (
            <div key={f.title} className="rounded-xl border border-[var(--border)] p-5 backdrop-blur-sm" style={{ background: CARD_BG }}>
              <div className="text-2xl">{f.icon}</div>
              <h3 className="font-display mt-3 text-lg font-bold">{f.title}</h3>
              <p className="mt-1 text-sm leading-relaxed text-[var(--text-secondary)]">{f.body}</p>
            </div>
          ))}
        </section>

        {/* Pricing */}
        <section className="mt-28 text-center">
          <h2 className="font-display text-3xl font-bold md:text-4xl">Free to use</h2>
          <p className="mx-auto mt-3 max-w-xl text-[var(--text-secondary)]">
            The whole tracker is free. AI nutrition runs on Claude, which costs per lookup — so a small Pro tier may
            arrive for power users to cover those token costs. Casual use stays free.
          </p>
          <div className="mx-auto mt-10 grid max-w-3xl gap-4 md:grid-cols-2">
            <div className="rounded-2xl border-2 p-6 text-left backdrop-blur-sm" style={{ background: CARD_BG, borderColor: 'var(--accent-teal)' }}>
              <div className="flex items-baseline justify-between">
                <h3 className="font-display text-xl font-bold">Free</h3>
                <span className="font-display text-2xl font-extrabold text-[var(--accent-teal)]">$0</span>
              </div>
              <ul className="mt-4 space-y-2 text-sm text-[var(--text-secondary)]">
                <li>✓ Unlimited logging, history &amp; analytics</li>
                <li>✓ Personalised BMR / TDEE targets</li>
                <li>✓ AI nutrition lookups (fair use)</li>
                <li>✓ Password lock, backups &amp; export</li>
              </ul>
              <button onClick={createNew} className="btn btn-primary mt-6 w-full">Start free</button>
            </div>
            <div className="relative rounded-2xl border border-[var(--border)] p-6 text-left backdrop-blur-sm" style={{ background: CARD_BG }}>
              <span className="absolute right-4 top-4 rounded-full px-3 py-1 text-xs font-semibold text-[var(--activity-intake)]" style={{ background: 'rgba(167, 139, 250, 0.18)' }}>
                Coming soon
              </span>
              <h3 className="font-display text-xl font-bold">Pro</h3>
              <ul className="mt-4 space-y-2 text-sm text-[var(--text-secondary)]">
                <li>✦ High-volume AI nutrition lookups</li>
                <li>✦ Priority processing</li>
                <li>✦ Supports development &amp; covers token costs</li>
                <li>✦ Keeps wlns.app independent &amp; ad-free</li>
              </ul>
              <button disabled className="btn btn-secondary mt-6 w-full opacity-60">Not yet available</button>
            </div>
          </div>
        </section>

        {/* Closing CTA */}
        <section className="mt-28 rounded-3xl border border-[var(--border)] p-10 text-center backdrop-blur-md" style={{ background: CARD_BG }}>
          <h2 className="font-display text-3xl font-bold md:text-4xl">Start in five seconds.</h2>
          <p className="mx-auto mt-3 max-w-md text-[var(--text-secondary)]">No email. No password. Just your key and your data.</p>
          <button onClick={createNew} className="btn btn-primary mx-auto mt-6 px-8 py-3 text-base font-semibold">
            Create your log
          </button>
        </section>
      </div>
    </PublicLayout>
  )
}
